import React, { ReactElement } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { IRouteItem } from '../../../../routes/interfaces/IRouteItem';
import styles from './menu-item.module.scss';
import { getRootUrl } from '../../../../misc';
import { FormattedMessage } from 'react-intl';

interface BaseMenuItemProps {
    /**
     * Uses as LocalizationKey
     */
    name: string;
    defaultSvg?: ReactElement;
    secondarySvg?: ReactElement;
    url?: string;
    open?: boolean;
    children?: React.ReactNode;
    callback?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    childrenUrls?: string[];
}

const someChildIsSelected = (childrenUrls?: string[]): boolean => {
    if (childrenUrls && childrenUrls.length > 0) {
        return childrenUrls.some((url) => url === getRootUrl(location.pathname));
    }

    return false;
};

const getNormalizedPathname = (pathname: string): string => {
    const trimmedPath = pathname.replace(/\/+$/, '');
    return trimmedPath === '' ? '/' : trimmedPath;
};

const isProfileMenuItemActive = (name: string): boolean => {
    if (name.toLowerCase() !== 'profile') {
        return false;
    }

    const normalizedPath = getNormalizedPathname(location.pathname);
    return normalizedPath === '/' || normalizedPath.endsWith('/profile');
};

const getMenuUrl = (url?: string, childrenUrls?: string[]): string => {
    if (url) {
        return url;
    }

    if (childrenUrls && childrenUrls.length > 0) {
        return childrenUrls[0];
    }

    return '#';
};

export const BaseMenuItem: React.FC<BaseMenuItemProps> = (props): ReactElement => {
    const { name, defaultSvg, secondarySvg, url, open, callback, children, childrenUrls } = props;

    // menu item is selected or some of children of menu item is selected
    const isActiveMenuItem = isProfileMenuItemActive(name) ||
        (url && url === getRootUrl(location.pathname)) ||
        (childrenUrls && childrenUrls.length > 0 && someChildIsSelected(childrenUrls));

    const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
        if (callback) {
            callback(event);
        }
    };

    const isCollapsed = open === false;

    return (
        <div className={styles.container}>
            <Link
                to={getMenuUrl(url, childrenUrls)}
                onClick={handleClick}
                className={classNames({
                    [styles.menuItem]: true,
                    [styles.active]: isActiveMenuItem,
                    [styles.collapsed]: isCollapsed,
                    [styles.expanded]: !isCollapsed,
                })}
            >
                {!secondarySvg 
                    ? <span className={styles.svg}>{defaultSvg}</span>
                    : open ? <span className={styles.svg}>{secondarySvg}</span> : <span className={styles.svg}>{defaultSvg}</span>}
                {name && (open === undefined || open) ? (
                    <span className={styles.text}><FormattedMessage id={name} /></span>
                ) : null}
            </Link>
            {children && <div className={styles.childrenContainer}>
                {childrenUrls && childrenUrls.length > 0 && (open === undefined || open) ? children : null}
            </div>}
        </div>
    );
};

interface MenuItemProps {
    route: IRouteItem;
    open?: boolean;
    callback?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    children?: React.ReactNode;
    childrenUrls?: string[];
}

export const MenuItem: React.FC<MenuItemProps> = (props): ReactElement => {
    const { route, open, callback, children, childrenUrls } = props;

    return (
        <BaseMenuItem
            name={route.name}
            defaultSvg={route.svg}
            url={route.url}
            open={open}
            callback={callback}
            childrenUrls={childrenUrls}
        >
            {children}
        </BaseMenuItem>
    );
};
